import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Trash2,
  Calendar,
  Plus,
  ClipboardList,
  Users,
  Building2,
  MapPin,
  ChevronDown,
  ChevronUp,
  ListTodo,
  Target,
  Truck,
  DollarSign,
  Clock,
  Sparkles,
  Globe,
  Tag,
  Gift,
  Eye,
  EyeOff,
  X,
  ArrowLeft,
  Send,
  Pencil
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { UserProfilePopover } from '@/components/ui/UserProfilePopover';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { EventTemplatePicker } from '@/components/events/EventTemplatePicker';
import { EventForm } from '@/pages/EventForm';
import { celebrationService } from '@/lib/api/celebration.service';
import { activityService } from '@/lib/api/activity.service';
import { eventWallApiService, type EventWallModerationPost } from '@/lib/api/eventWall.service';
import { goalService } from '@/lib/api/goal.service';
import { guestService } from '@/lib/api/guest.service';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { celebrationCelebrantService } from '@/lib/api/celebration-celebrant.service';
import { celebrationRegistryService } from '@/lib/api/celebrationRegistry.service';
import { useTeamMemberRole } from '@/hooks/useTeamMemberRole';
import { canEditCelebration, canDeleteCelebration } from '@/utils/permissions';
import { formatCelebrationDates, formatEventDateTime, formatActivityTimeRange, formatEventDateRange } from '@/utils/dateFormat';
import type { CelebrationWithEvents } from '@/types/celebration.types';
import type { TagsSnapshot } from '@/types/tag.types';
import type { Activity } from '@/types/activity.types';
import type { GoalInstanceWithDetails } from '@/types/goal.types';
import type { Guest } from '@/types/guest.types';
import type { TeamMemberWithUser } from '@/types/teamMember.types';
import type { CelebrationCelebrant } from '@/types/user-contact.types';
import type { CelebrationRegistry } from '@/types/celebrationRegistry.types';
import { EventListsPanel } from '@/components/celebrations/EventListsPanel';

const getCelebrationTheme = (type: string) => {
  const themes: Record<string, { gradient: string; border: string; accent: string; ring: string; badgeBg: string }> = {
    wedding:     { gradient: 'from-rose-100 via-pink-50 to-white',    border: 'border-rose-100',   accent: 'text-rose-600',   ring: 'ring-rose-200',   badgeBg: 'bg-rose-50' },
    birthday:    { gradient: 'from-amber-100 via-orange-50 to-white', border: 'border-amber-100',  accent: 'text-amber-600',  ring: 'ring-amber-200',  badgeBg: 'bg-amber-50' },
    graduation:  { gradient: 'from-violet-100 via-purple-50 to-white',border: 'border-violet-100', accent: 'text-violet-600', ring: 'ring-violet-200', badgeBg: 'bg-violet-50' },
    anniversary: { gradient: 'from-emerald-100 via-teal-50 to-white', border: 'border-emerald-100',accent: 'text-emerald-600',ring: 'ring-emerald-200', badgeBg: 'bg-emerald-50' },
    baby_shower: { gradient: 'from-sky-100 via-blue-50 to-white',     border: 'border-sky-100',    accent: 'text-sky-600',    ring: 'ring-sky-200',    badgeBg: 'bg-sky-50' },
    default:     { gradient: 'from-blue-100 via-indigo-50 to-white',  border: 'border-blue-100',   accent: 'text-blue-600',   ring: 'ring-blue-200',   badgeBg: 'bg-blue-50' },
  };
  return themes[type] ?? themes.default;
};

const formatStatusLabel = (status?: string): string => {
  if (!status) return 'draft';
  return status.replace(/_/g, ' ');
};

const getStatusPillClass = (status?: string): string => {
  switch (status) {
    case 'draft':     return 'border-slate-300  bg-slate-100   text-slate-600';
    case 'planning':  return 'border-blue-200   bg-blue-50     text-blue-700';
    case 'planned':   return 'border-violet-200 bg-violet-50   text-violet-700';
    case 'active':    return 'border-emerald-300 bg-emerald-50 text-emerald-700';
    case 'completed': return 'border-teal-200   bg-teal-50     text-teal-700';
    case 'archived':  return 'border-slate-300  bg-slate-200   text-slate-500';
    case 'cancelled': return 'border-red-200    bg-red-50      text-red-600';
    default:          return 'border-slate-300  bg-white/80    text-slate-900';
  }
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const CelebrationDetails: React.FC = () => {
  const { t } = useTranslation(['celebrations', 'common']);
  const params = useParams();
  const { id } = params;
  const navigate = useNavigate();
  const [celebration, setCelebration] = useState<CelebrationWithEvents | null>(null);
  const [celebrants, setCelebrants] = useState<CelebrationCelebrant[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [eventActivities, setEventActivities] = useState<{ [eventId: number]: Activity[] }>({});
  const [expandedActivityPanels, setExpandedActivityPanels] = useState<{ [eventId: number]: boolean }>({});
  const [expandedListPanels, setExpandedListPanels] = useState<{ [eventId: number]: boolean }>({});
  const [showCelebrationTemplatePicker, setShowCelebrationTemplatePicker] = useState(false);
  const [showManualEventPopup, setShowManualEventPopup] = useState(false);
  const [tagsConfirmed, setTagsConfirmed] = useState<boolean | null>(null);
  const [tagsSnapshot, setTagsSnapshot] = useState<TagsSnapshot | null>(null);
  const [registries, setRegistries] = useState<CelebrationRegistry[]>([]);
  const [goals, setGoals] = useState<GoalInstanceWithDetails[]>([]);
  const [rsvpSummary, setRsvpSummary] = useState({ accepted: 0, invited: 0, declined: 0, pending: 0 });
  const [wallPosts, setWallPosts] = useState<EventWallModerationPost[]>([]);
  const [wallLoading, setWallLoading] = useState(false);
  const [wallError, setWallError] = useState<string | null>(null);
  const [wallActionPostId, setWallActionPostId] = useState<number | null>(null);
  const [wallPreapproveEnabled, setWallPreapproveEnabled] = useState(false);
  const [wallPostEventWindowDays, setWallPostEventWindowDays] = useState(0);
  const [wallSettingsSaving, setWallSettingsSaving] = useState(false);
  const [celebrationStats, setCelebrationStats] = useState({
    totalGuests: 0,
    totalTasks: 0,
    completedTasks: 0,
    totalServices: 0
  });

  const celebrationIdNum = id ? parseInt(id) : 0;
  const { role } = useTeamMemberRole(celebrationIdNum);
  const canEdit = canEditCelebration(role);
  const canDelete = canDeleteCelebration(role);

  const backToTemplateSelection = useCallback(() => {
    setShowManualEventPopup(false);
    setShowCelebrationTemplatePicker(true);
  }, []);

  const loadWallPosts = useCallback(async (slug?: string) => {
    if (!slug) {
      setWallPosts([]);
      setWallError(null);
      return;
    }

    try {
      setWallLoading(true);
      setWallError(null);
      const posts = await eventWallApiService.listPosts(slug);
      setWallPosts(posts);
    } catch (err) {
      setWallError(err instanceof Error ? err.message : 'Failed to load wall posts.');
    } finally {
      setWallLoading(false);
    }
  }, []);

  const loadWallSettings = useCallback(async (slug?: string) => {
    if (!slug) {
      setWallPreapproveEnabled(false);
      setWallPostEventWindowDays(0);
      return;
    }

    try {
      const settings = await eventWallApiService.getSettings(slug);
      setWallPreapproveEnabled(Boolean(settings.wall_preapprove_enabled));
      setWallPostEventWindowDays(Number.isInteger(settings.wall_post_event_window_days) ? settings.wall_post_event_window_days : 0);
    } catch {
      // Keep wall moderation usable even if settings load fails.
    }
  }, []);

  const loadCelebrationData = useCallback(async (celebrationId: number) => {
    try {
      setLoading(true);
      // 1 round-trip: celebration + events with pre-computed stats
      const data = await celebrationService.getCelebrationWithEvents(celebrationId);
      if (!data || !('id' in data) || data.id == null) {
        setError(t('celebrations:details.error.failedLoad'));
        setCelebration(null);
        return;
      }
      setCelebration(data);
      await Promise.all([
        loadWallPosts(data.slug),
        loadWallSettings(data.slug),
      ]);

      // Tags are already in the celebration response — no extra API call needed
      setTagsConfirmed(data.tags_confirmed ?? false);
      if (data.tags_confirmed && data.tags_snapshot && Object.keys(data.tags_snapshot).length > 0) {
        setTagsSnapshot(data.tags_snapshot);
      } else {
        setTagsSnapshot(null);
      }

      // Secondary data fetched in parallel (celebrants, team, registries)
      const [celebrantsData, teamData, registryData, goalsData, guestsData] = await Promise.all([
        celebrationCelebrantService.getCelebrants(celebrationId).catch(() => []),
        teamMemberService.getTeamMembers(celebrationId).catch(() => []),
        celebrationRegistryService.getRegistries(celebrationId).catch(() => []),
        goalService.getInstancesByEntity('celebration', celebrationId).catch(() => []),
        guestService.getCelebrationGuests(celebrationId).catch(() => []),
      ]);
      setCelebrants(celebrantsData || []);
      setTeamMembers(teamData || []);
      setRegistries(registryData || []);
      setGoals(goalsData || []);

      const accepted = (guestsData || []).filter((guest: Guest) => guest.rsvp_status === 'accepted').length;
      const declined = (guestsData || []).filter((guest: Guest) => guest.rsvp_status === 'declined').length;
      const invited = (guestsData || []).filter((guest: Guest) => guest.rsvp_status === 'invited').length;
      const pending = (guestsData || []).filter((guest: Guest) => guest.rsvp_status === 'new').length;
      setRsvpSummary({ accepted, declined, invited, pending });

      if (data.events && data.events.length > 0) {
        setExpandedActivityPanels({});

        // Single call: fetch all activities for the celebration at once
        const allActivities = await activityService.getActivitiesByCelebration(celebrationId);

        const activityMap: { [key: number]: Activity[] } = {};
        allActivities.forEach((activity) => {
          const eventId = activity.event_id;
          if (!activityMap[eventId]) {
            activityMap[eventId] = [];
          }
          activityMap[eventId].push(activity);
        });
        setEventActivities(activityMap);

        // Use pre-computed stats from API response — no extra fetches needed
        setCelebrationStats({
          totalGuests: data.total_guests,
          totalTasks: data.total_tasks,
          completedTasks: data.completed_tasks,
          totalServices: data.total_services
        });
      } else {
        setCelebrationStats({
          totalGuests: data.total_guests,
          totalTasks: data.total_tasks,
          completedTasks: data.completed_tasks,
          totalServices: data.total_services,
        });
      }
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error?.response?.data?.message || t('celebrations:details.error.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [loadWallPosts, loadWallSettings]);

  useEffect(() => {
    if (id) loadCelebrationData(parseInt(id));
  }, [id, loadCelebrationData]);

  const toggleActivityPanel = (eventId: number) => {
    setExpandedActivityPanels((prev) => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const toggleListPanel = (eventId: number) => {
    setExpandedListPanels((prev) => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const handleDelete = async () => {
    if (!celebration || !window.confirm(t('celebrations:details.confirmations.delete'))) return;
    try {
      setDeleteLoading(true);
      await celebrationService.deleteCelebration(celebration.id);
      navigate('/celebrations');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error?.response?.data?.message || t('celebrations:details.error.failedDelete'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const promotionTargetStatus = (() => {
    switch (celebration?.status) {
      case 'draft': return 'planning';
      case 'planning': return 'planned';
      case 'planned': return 'active';
      case 'active': return 'completed';
      default: return null;
    }
  })();

  const handlePromoteCelebration = async () => {
    if (!celebrationIdNum || !celebration) return;

    try {
      setIsPromoting(true);
      await celebrationService.promoteCelebration(celebrationIdNum);
      await loadCelebrationData(celebrationIdNum);
    } catch (err) {
      const apiError = err as {
        response?: {
          status?: number;
          data?: {
            message?: string;
          };
        };
      };

      const message = apiError?.response?.data?.message || 'Failed to promote celebration status.';
      const status = apiError?.response?.status;
      const isPromotionReadinessBlock =
        message.includes('requires all celebration tasks')
        || message.includes('cannot transition to planning')
        || (status === 400 && promotionTargetStatus === 'planning');

      if (isPromotionReadinessBlock) {
        navigate(`/celebrations/${celebrationIdNum}/promotion-readiness`);
        return;
      }

      setError(message);
    } finally {
      setIsPromoting(false);
    }
  };

  const handleToggleWallPreapprove = async (checked: boolean) => {
    if (!celebration?.slug) {
      return;
    }

    try {
      setWallSettingsSaving(true);
      setWallError(null);
      const updated = await eventWallApiService.updateSettings(celebration.slug, {
        wall_preapprove_enabled: checked,
      });
      setWallPreapproveEnabled(Boolean(updated.wall_preapprove_enabled));
    } catch (err) {
      setWallError(err instanceof Error ? err.message : 'Failed to update wall settings.');
    } finally {
      setWallSettingsSaving(false);
    }
  };

  const handleUpdateWallWindowDays = async () => {
    if (!celebration?.slug) {
      return;
    }

    const normalizedDays = Number.isInteger(wallPostEventWindowDays) && wallPostEventWindowDays >= 0
      ? wallPostEventWindowDays
      : 0;

    try {
      setWallSettingsSaving(true);
      setWallError(null);
      const updated = await eventWallApiService.updateSettings(celebration.slug, {
        wall_post_event_window_days: normalizedDays,
      });
      setWallPostEventWindowDays(updated.wall_post_event_window_days);
    } catch (err) {
      setWallError(err instanceof Error ? err.message : 'Failed to update wall settings.');
    } finally {
      setWallSettingsSaving(false);
    }
  };

  const handleToggleWallPostVisibility = async (post: EventWallModerationPost) => {
    if (!celebration?.slug) {
      return;
    }

    const nextHidden = !post.is_hidden;
    try {
      setWallActionPostId(post.id);
      setWallError(null);
      await eventWallApiService.setVisibility(celebration.slug, post.id, nextHidden);
      setWallPosts((current) => current.map((item) => (
        item.id === post.id ? { ...item, is_hidden: nextHidden } : item
      )));
    } catch (err) {
      setWallError(err instanceof Error ? err.message : 'Failed to update wall post visibility.');
    } finally {
      setWallActionPostId(null);
    }
  };

  if (loading) {
    return (
      <PageState
        kind="loading"
        width="wide"
        title={t('celebrations:details.loading.title')}
        description={t('celebrations:details.loading.description')}
      />
    );
  }

  if (error || !celebration) {
    return (
      <PageState
        kind="error"
        width="narrow"
        title={t('celebrations:details.error.title')}
        description={error || t('celebrations:details.error.notFound')}
      />
    );
  }

  const theme = getCelebrationTheme(celebration.celebration_type);
  const tagCount = tagsSnapshot ? Object.values(tagsSnapshot).reduce((sum, vals) => sum + vals.length, 0) : 0;
  const totalGoals = goals.length;
  const completedGoals = goals.filter((goal) => goal.status === 'completed').length;
  const totalBudget = celebration.budget_summary?.estimated_total ?? celebration.budget ?? 0;
  const committedBudget = celebration.budget_summary?.committed_total ?? 0;
  const budgetPct = totalBudget > 0 ? Math.round((committedBudget / totalBudget) * 100) : 0;
  const celebrantUserIds = new Set(
    celebrants
      .map((celebrant) => celebrant.user_contact?.user_id ?? celebrant.user_id)
      .filter((userId): userId is number => typeof userId === 'number')
  );
  const displayableTeamMembers = teamMembers.filter((member) => !celebrantUserIds.has(member.user.id));
  const organizerMembers = displayableTeamMembers.filter((member) => member.role === 'organizer');
  const eventManagerMembers = displayableTeamMembers.filter((member) => member.role === 'event_manager');

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs
        items={[
          { label: t('common:nav.home'), to: '/home' },
          { label: celebration.title },
        ]}
      />

      {/* Hero Header */}
      <HeroQuickMenuCard
        borderClassName={theme.border}
        heroClassName={`bg-gradient-to-br ${theme.gradient}`}
        columns={7}
        stripClassName="flex-wrap overflow-hidden [&>*]:min-w-[6.25rem] [&>*>div]:py-2 [&>*>div]:px-1"
        heroContent={
          <div className="flex items-start">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="text-xl font-bold tracking-tight text-foreground">{celebration.title}</h1>
                      {celebration.created_via === 'quick_create' && (
                        <Badge variant="secondary" size="sm" className="bg-amber-100 text-amber-700 border-amber-200">
                          ⚡ Quick
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                    <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 ${getStatusPillClass(celebration.status)}`}>
                      <span className="text-sm font-semibold capitalize">{formatStatusLabel(celebration.status)}</span>
                    </div>
                    {promotionTargetStatus && (
                      <Button
                        size="sm"
                        className="h-8 px-3.5 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                        onClick={handlePromoteCelebration}
                        disabled={isPromoting}
                      >
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                        {isPromoting ? 'Promoting…' : `Promote to ${formatStatusLabel(promotionTargetStatus)}`}
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 bg-white hover:bg-slate-50 border-slate-300"
                        onClick={() => navigate(`/celebrations/${celebration.id}/edit#basic-details`)}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        {t('common:actions.edit', 'Edit')}
                      </Button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deleteLoading}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-red-50 text-xs text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50 shrink-0"
                        title="Delete Celebration"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                  <span className="inline-flex items-center gap-1.5 font-medium text-slate-700">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    {celebration.start_date ? formatCelebrationDates(celebration.start_date, celebration.end_date) : 'No date set'}
                  </span>
                  {celebration.location_name && (
                    <span className="inline-flex items-center gap-1.5 font-medium text-slate-700">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      {celebration.location_name}
                    </span>
                  )}
                  {celebrants.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-slate-600">
                      <Gift className="h-3.5 w-3.5 text-rose-500" />
                      <span>Celebrant{celebrants.length > 1 ? 's' : ''}: <strong>{celebrants.map(c => c.user_contact?.full_name || 'Unknown').join(', ')}</strong></span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
        items={(() => {
          const isQuick = celebration.created_via === 'quick_create';
          const flags = celebration.feature_profile?.module_flags;
          const allItems = [
            {
              key: 'guests',
              icon: Users,
              iconClassName: 'text-blue-500',
              hoverClassName: 'hover:bg-blue-50',
              value: rsvpSummary.accepted > 0
                ? `${rsvpSummary.accepted} Accepted`
                : `${celebrationStats.totalGuests} Guests`,
              label: t('celebrations:details.stats.guests'),
              href: `/celebrations/${celebration.id}/guests`,
            },
            {
              key: 'tasks',
              icon: ListTodo,
              iconClassName: 'text-amber-500',
              hoverClassName: 'hover:bg-amber-50',
              value: `${celebrationStats.totalTasks - celebrationStats.completedTasks}/${celebrationStats.totalTasks}`,
              label: t('celebrations:details.stats.tasksLeft'),
              href: `/celebrations/${celebration.id}/tasks`,
            },
            {
              key: 'goals',
              icon: Target,
              iconClassName: 'text-violet-500',
              hoverClassName: 'hover:bg-violet-50',
              value: totalGoals > 0 ? `${completedGoals}/${totalGoals} Done` : t('celebrations:details.stats.goalsOpen'),
              label: t('celebrations:details.stats.goals'),
              href: `/celebrations/${celebration.id}/goals`,
              hiddenWhenQuick: flags && !flags.goals,
            },
            {
              key: 'services',
              icon: Truck,
              iconClassName: 'text-purple-500',
              hoverClassName: 'hover:bg-purple-50',
              value: celebrationStats.totalServices,
              label: t('celebrations:details.stats.services'),
              href: `/celebrations/${celebration.id}/services`,
              hiddenWhenQuick: flags && !flags.services,
            },
            {
              key: 'budget',
              icon: DollarSign,
              iconClassName: 'text-teal-500',
              hoverClassName: 'hover:bg-teal-50',
              value: totalBudget > 0 ? `${formatCurrency(totalBudget)} (${budgetPct}%)` : 'Open',
              label: t('celebrations:details.stats.budget', 'Budget'),
              href: `/celebrations/${celebration.id}/budget`,
              hiddenWhenQuick: flags && !flags.budget,
            },
            {
              key: 'venue-management',
              icon: Building2,
              iconClassName: 'text-indigo-500',
              hoverClassName: 'hover:bg-indigo-50',
              value: t('celebrations:details.stats.venuesManage'),
              label: t('celebrations:details.stats.venues'),
              href: `/celebrations/${celebration.id}/venues`,
              hiddenWhenQuick: !flags || !flags.venues,
            },
            {
              key: 'tags',
              icon: Tag,
              iconClassName: 'text-emerald-500',
              hoverClassName: 'hover:bg-emerald-50',
              value: tagCount,
              label: t('celebrations:details.stats.tags'),
              href: `/celebrations/${celebration.id}/setup`,
            },
            {
              key: 'guest-page',
              icon: Globe,
              iconClassName: 'text-teal-500',
              hoverClassName: 'hover:bg-teal-50',
              value: celebration.slug ? t('celebrations:details.stats.guestPageActive') : t('celebrations:details.stats.guestPageSetup'),
              label: t('celebrations:details.stats.guestPage'),
              href: `/celebrations/${celebration.id}/guest-landing`,
            },
            {
              key: 'invitations',
              icon: Send,
              iconClassName: 'text-blue-500',
              hoverClassName: 'hover:bg-blue-50',
              value: 'Manage',
              label: 'Invitations',
              href: `/celebrations/${celebration.id}/invitations`,
            },
            {
              key: 'registries',
              icon: Gift,
              iconClassName: 'text-pink-500',
              hoverClassName: 'hover:bg-pink-50',
              value: registries.length,
              label: t('celebrations:details.stats.registries'),
              href: `/celebrations/${celebration.id}/registries`,
              hiddenWhenQuick: !flags || !flags.registry,
            },
          ];
          return isQuick
            ? allItems.filter(item => !item.hiddenWhenQuick).map(({ hiddenWhenQuick: _h, ...rest }) => rest)
            : allItems.map(({ hiddenWhenQuick: _h, ...rest }) => rest);
        })()}
      />

      {/* Tags Setup Banner */}
      {tagsConfirmed === false && canEdit && (
        <div className="relative overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50">
          <div className="absolute right-0 top-0 h-full w-32 opacity-10 bg-[radial-gradient(circle_at_80%_50%,_#f59e0b_0%,_transparent_70%)]" />
          <div className="flex items-center justify-between px-5 py-4 relative">
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-900">{t('celebrations:details.tagsSetupBanner.title')}</p>
                <p className="text-xs text-amber-600 mt-0.5">{t('celebrations:details.tagsSetupBanner.description')}</p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-white border-0 flex-shrink-0"
              onClick={() => navigate(`/celebrations/${celebration.id}/setup`)}
            >
              <Sparkles className="h-3 w-3 mr-1.5" /> {t('celebrations:details.tagsSetupBanner.button')}
            </Button>
          </div>
        </div>
      )}

      {/* Main Content Grid: 2/3 Events, 1/3 Team & Operations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left / Main Column: Events */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-primary/5 to-transparent">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-primary" />
                </div>
                <span className="font-semibold text-sm">{t('celebrations:details.events.header')}</span>
                <Badge variant="secondary" size="sm" className="rounded-full">{celebration.events?.length || 0}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setShowCelebrationTemplatePicker(true)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />{t('celebrations:details.events.add')}
                </Button>
              </div>
            </div>

            {(!celebration.events || celebration.events.length === 0) ? (
              <div className="text-center py-12 px-4">
                <div className="text-4xl mb-2">📅</div>
                <p className="text-sm font-medium">{t('celebrations:details.events.empty.title')}</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">{t('celebrations:details.events.empty.description')}</p>
                <Button
                  size="sm"
                  className="mt-4"
                  onClick={() => setShowCelebrationTemplatePicker(true)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  {t('celebrations:details.events.empty.button')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3.5 p-4">
                {celebration.events.map((event) => {
                  const eventStatus = event.status ?? 'draft';
                  const eventTitle = event.title ?? ('event_name' in event ? event.event_name : 'Untitled event');
                  const eventDate = event.event_date ?? ('date' in event ? event.date : undefined);
                  const isActiveEvent = eventStatus.toLowerCase() === 'active';
                  const isActivitiesExpanded = !!expandedActivityPanels[event.id];
                  const isListsExpanded = !!expandedListPanels[event.id];
                  const activities = eventActivities[event.id] || [];
                  const guestCount = event.guest_count ?? 0;
                  const completedTaskCount = event.completed_task_count ?? 0;
                  const pendingTaskCount = event.pending_task_count ?? 0;
                  const totalTaskCount = completedTaskCount + pendingTaskCount;
                  const serviceCount = event.confirmed_service_count ?? 0;

                  return (
                    <div
                      key={event.id}
                      className={`group overflow-hidden rounded-xl border shadow-sm transition-all ${
                        isActiveEvent
                          ? 'border-primary/20 bg-white hover:border-primary/30 hover:shadow-md'
                          : 'border-border/70 bg-muted/10'
                      }`}
                    >
                      <div className={isActiveEvent ? 'bg-gradient-to-r from-primary/[0.04] via-white to-transparent' : ''}>
                        <div className="px-4 py-3.5 sm:px-5">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <button
                                    type="button"
                                    className="truncate text-left text-base font-semibold text-foreground transition-colors hover:text-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/events/${event.id}`);
                                    }}
                                  >
                                    {eventTitle}
                                  </button>
                                  <Badge variant={eventStatus as any} size="sm">{eventStatus}</Badge>
                                  {!isActiveEvent && <Badge variant="outline" size="sm">{t('celebrations:details.events.previewOnly')}</Badge>}
                                </div>

                                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                  <span className="inline-flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5 text-muted-foreground/70" />
                                    {event.start_time && event.end_time
                                      ? formatEventDateRange(event.start_time, event.end_time)
                                      : formatEventDateTime(eventDate, true)}
                                  </span>
                                  {event.venue_name && (
                                    <span className="inline-flex items-center gap-1.5 truncate max-w-[220px]" title={event.venue_name}>
                                      <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/70" />
                                      <span className="truncate">{event.venue_name}</span>
                                    </span>
                                  )}
                                </div>

                                <div className="mt-3 flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                                      isActivitiesExpanded
                                        ? 'border-primary/30 bg-primary/10 text-primary font-medium'
                                        : 'border-primary/15 bg-primary/[0.06] text-primary hover:bg-primary/[0.1]'
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleActivityPanel(event.id);
                                    }}
                                  >
                                    <Calendar className="h-3.5 w-3.5" />
                                    <span>{t('celebrations:details.events.pills.activities')}</span>
                                    <span className="font-semibold">{activities.length}</span>
                                    {isActivitiesExpanded ? <ChevronUp className="h-3 w-3 ml-0.5" /> : <ChevronDown className="h-3 w-3 ml-0.5" />}
                                  </button>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-xs text-sky-800 transition-colors hover:bg-sky-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/events/${event.id}/guests`);
                                    }}
                                  >
                                    <Users className="h-3.5 w-3.5 text-sky-600" />
                                    <span>{t('celebrations:details.events.pills.guests')}</span>
                                    <span className="font-semibold">{guestCount}</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs text-amber-800 transition-colors hover:bg-amber-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/events/${event.id}/tasks`);
                                    }}
                                  >
                                    <ListTodo className="h-3.5 w-3.5 text-amber-600" />
                                    <span>{t('celebrations:details.events.pills.tasks')}</span>
                                    <span className="font-semibold">{completedTaskCount}/{totalTaskCount}</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-0.5 text-xs text-violet-800 transition-colors hover:bg-violet-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/events/${event.id}/services`);
                                    }}
                                  >
                                    <Truck className="h-3.5 w-3.5 text-violet-600" />
                                    <span>{t('celebrations:details.events.pills.services')}</span>
                                    <span className="font-semibold">{serviceCount}</span>
                                  </button>
                                  <button
                                    type="button"
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                                      isListsExpanded
                                        ? 'border-sky-300 bg-sky-100 text-sky-800 font-medium'
                                        : 'border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100'
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleListPanel(event.id);
                                    }}
                                  >
                                    <ClipboardList className="h-3.5 w-3.5 text-sky-600" />
                                    <span>Lists</span>
                                    {isListsExpanded ? <ChevronUp className="h-3 w-3 ml-0.5" /> : <ChevronDown className="h-3 w-3 ml-0.5" />}
                                  </button>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 self-start shrink-0">
                                <Button
                                  variant="outline"
                                  size="xs"
                                  className="rounded-lg bg-white shadow-none hover:bg-muted"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/events/${event.id}`);
                                  }}
                                >
                                  {t('celebrations:details.events.actions.view')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {isActivitiesExpanded && (
                          <div className="border-t border-primary/10 bg-gradient-to-b from-primary/[0.02] to-white">
                            <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
                              <div className="rounded-xl border border-primary/10 bg-white/95 px-4 py-3.5 shadow-inner shadow-primary/5">
                                {activities.length > 0 ? (
                                  <div className="relative">
                                    {(() => {
                                      // Group and sort activities for hierarchy display
                                      const getTimestamp = (d?: string) => d ? new Date(d).getTime() : 0;
                                      const sortedRaw = [...activities].sort((a, b) =>
                                        getTimestamp(a.start_time) - getTimestamp(b.start_time)
                                      );

                                      const ids = new Set(sortedRaw.map((activity) => activity.id));
                                      const childrenMap: Record<number, typeof activities> = {};
                                      const roots: typeof activities = [];

                                      sortedRaw.forEach((activity) => {
                                        if (activity.parent_activity_id && ids.has(activity.parent_activity_id)) {
                                          if (!childrenMap[activity.parent_activity_id]) childrenMap[activity.parent_activity_id] = [];
                                          childrenMap[activity.parent_activity_id].push(activity);
                                        } else {
                                          roots.push(activity);
                                        }
                                      });

                                      const orderedActivities: (typeof activities[0] & { depth: number })[] = [];
                                      roots.forEach((root) => {
                                        orderedActivities.push({ ...root, depth: 0 });
                                        if (childrenMap[root.id]) {
                                          childrenMap[root.id].forEach((child) => orderedActivities.push({ ...child, depth: 1 }));
                                        }
                                      });

                                      return orderedActivities.map((activity, index) => {
                                        const isLast = index === orderedActivities.length - 1;
                                        const isChild = activity.depth > 0;

                                        return (
                                          <div key={activity.id} className="flex gap-3">
                                            <div className="flex flex-col items-center">
                                              <div className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                                                activity.status === 'published' ? 'bg-green-500' :
                                                activity.status === 'planned' ? 'bg-blue-400' : 'bg-gray-400'
                                              } ${isChild ? 'h-2 w-2' : ''}`} />
                                              {!isLast && <div className="w-0.5 flex-1 min-h-[20px] bg-primary/10" />}
                                            </div>
                                            <div
                                              className={`flex-1 rounded-lg border border-transparent px-2.5 py-1.5 transition-colors cursor-pointer hover:border-primary/10 hover:bg-primary/[0.03] ${isChild ? 'ml-3' : ''}`}
                                              onClick={() => navigate(`/activities/${activity.id}`)}
                                            >
                                              <div className="flex flex-wrap items-center gap-2">
                                                <span className={`text-sm ${isChild ? 'text-muted-foreground' : 'font-medium text-foreground'} truncate`}>
                                                  {activity.title}
                                                </span>
                                                {activity.status === 'draft' && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">draft</span>}
                                              </div>
                                              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                                <span className="font-mono">{formatActivityTimeRange(activity.start_time, activity.end_time)}</span>
                                                {activity.location && <span>📍 {activity.location}</span>}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      });
                                    })()}
                                  </div>
                                ) : (
                                  <div className="rounded-lg border border-dashed border-primary/15 bg-primary/[0.02] px-4 py-4 text-center">
                                    <p className="text-sm font-medium text-foreground">{t('celebrations:details.events.activities.empty.title')}</p>
                                    <p className="mt-0.5 text-xs text-muted-foreground">{t('celebrations:details.events.activities.empty.description')}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {isListsExpanded && (
                          <div className="border-t border-sky-100 bg-gradient-to-b from-sky-50/40 to-white">
                            <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
                              <div className="rounded-xl border border-sky-100 bg-white/95 px-4 py-3.5 shadow-inner shadow-sky-500/5">
                                <EventListsPanel eventId={event.id} />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right / Sidebar Column: Team & Engagement */}
        <div className="space-y-4">
          {/* Team & Celebrants Card */}
          <div className={`rounded-xl border bg-white shadow-sm overflow-hidden ${theme.border}`}>
            <div className="px-4 py-3 border-b bg-gradient-to-r from-slate-50 to-transparent flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-600" />
                <h2 className="text-sm font-semibold text-slate-800">Team & Celebrants</h2>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => navigate(`/celebrations/${celebration.id}/edit#celebration-team-members`)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Manage
                </button>
              )}
            </div>
            <div className="p-4 space-y-3.5">
              {celebrants.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-rose-500 uppercase tracking-wider mb-1.5">
                    Celebrants ({celebrants.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {celebrants.map((celebrant) => {
                      const name = celebrant.user_contact?.full_name || 'Unknown';
                      return (
                        <UserProfilePopover
                          key={celebrant.id}
                          user={{
                            id: celebrant.user_contact?.user_id || celebrant.user_id,
                            full_name: name,
                            email: celebrant.user_contact?.email,
                            phone: celebrant.user_contact?.phone
                          }}
                          celebrationId={celebrationIdNum}
                        >
                          <div className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50/70 px-2.5 py-1 text-xs text-rose-900 cursor-pointer hover:bg-rose-100 transition-colors">
                            <Gift className="h-3 w-3 text-rose-500" />
                            <span className="font-medium">{name}</span>
                            {celebrant.role && <span className="text-[10px] text-rose-600">({celebrant.role})</span>}
                          </div>
                        </UserProfilePopover>
                      );
                    })}
                  </div>
                </div>
              )}

              {organizerMembers.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wider mb-1.5">
                    {t('celebrations:details.roles.organizer')} ({organizerMembers.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {organizerMembers.map((member) => (
                      <UserProfilePopover key={member.user.id} user={member.user} celebrationId={celebrationIdNum}>
                        <div className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50/70 px-2.5 py-1 text-xs text-indigo-900 cursor-pointer hover:bg-indigo-100 transition-colors">
                          <Users className="h-3 w-3 text-indigo-600" />
                          <span className="font-medium">{member.user.full_name}</span>
                        </div>
                      </UserProfilePopover>
                    ))}
                  </div>
                </div>
              )}

              {eventManagerMembers.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider mb-1.5">
                    {t('celebrations:details.roles.eventManager')} ({eventManagerMembers.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {eventManagerMembers.map((member) => (
                      <UserProfilePopover key={member.user.id} user={member.user} celebrationId={celebrationIdNum}>
                        <div className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50/70 px-2.5 py-1 text-xs text-emerald-900 cursor-pointer hover:bg-emerald-100 transition-colors">
                          <Users className="h-3 w-3 text-emerald-600" />
                          <span className="font-medium">{member.user.full_name}</span>
                        </div>
                      </UserProfilePopover>
                    ))}
                  </div>
                </div>
              )}

              {celebrants.length === 0 && organizerMembers.length === 0 && eventManagerMembers.length === 0 && (
                <div className="text-xs text-muted-foreground">No team members or celebrants added yet.</div>
              )}
            </div>
          </div>

          {/* Wall Moderation */}
          <div className={`rounded-xl border bg-white shadow-sm overflow-hidden ${theme.border}`}>
            <div className="px-3 pt-3 pb-2 border-b bg-slate-50/70 flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5 text-indigo-600" />
              <span className="text-xs font-medium">Wall Moderation</span>
              {wallPosts.length > 0 && (
                <Badge variant="secondary" size="sm" className="ml-1 px-1 py-0 text-[10px] h-4">
                  {wallPosts.length}
                </Badge>
              )}
            </div>

            <div className="p-4 m-0 space-y-3">
                {!celebration.slug ? (
                  <div className="text-xs text-muted-foreground py-2">Wall moderation is unavailable until this celebration has a public slug.</div>
                ) : wallLoading ? (
                  <div className="text-xs text-muted-foreground">Loading wall posts...</div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-2.5 space-y-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-xs font-medium text-slate-900">Pre-approve posts</div>
                          <div className="text-[10px] text-muted-foreground">Hide new posts until reviewed</div>
                        </div>
                        <Switch checked={wallPreapproveEnabled} disabled={wallSettingsSaving} onCheckedChange={handleToggleWallPreapprove} />
                      </div>
                      <div className="flex items-center gap-2 pt-1 border-t border-slate-200/60">
                        <label className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-1">
                          Post window:
                          <input
                            type="number"
                            min={0}
                            step={1}
                            className="w-14 rounded border border-slate-300 px-1.5 py-0.5 text-xs text-center"
                            value={wallPostEventWindowDays}
                            onChange={(event) => setWallPostEventWindowDays(Math.max(0, Number.parseInt(event.target.value || '0', 10) || 0))}
                            disabled={wallSettingsSaving}
                          />
                          days
                        </label>
                        <Button variant="outline" size="xs" className="h-6 text-[11px]" disabled={wallSettingsSaving} onClick={handleUpdateWallWindowDays}>
                          Save
                        </Button>
                      </div>
                    </div>

                    {wallError && <div className="text-xs text-destructive">{wallError}</div>}

                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">Posts ({wallPosts.length})</span>
                      <Button variant="ghost" size="xs" className="h-6 text-[11px]" disabled={wallLoading || !celebration.slug} onClick={() => loadWallPosts(celebration.slug)}>
                        Refresh
                      </Button>
                    </div>

                    {wallPosts.length === 0 ? (
                      <div className="text-xs text-muted-foreground py-2">No wall posts yet.</div>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                        {wallPosts.map((post) => (
                          <div key={post.id} className="rounded-lg border border-slate-200 p-2.5 space-y-1.5 bg-slate-50/40 text-xs">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-medium text-slate-900">{post.guest_name}</div>
                                <div className="text-[10px] text-muted-foreground">{new Date(post.created_at).toLocaleString()}</div>
                              </div>
                              <Badge variant={post.is_hidden ? 'secondary' : 'active'} size="sm" className="text-[10px]">
                                {post.is_hidden ? 'Hidden' : 'Visible'}
                              </Badge>
                            </div>
                            <p className="text-slate-700 whitespace-pre-wrap text-xs">{post.post_text}</p>
                            {post.photo_url && (
                              <img src={post.photo_url} alt={`Wall photo from ${post.guest_name}`} className="max-h-32 rounded border border-slate-200 object-cover" loading="lazy" />
                            )}
                            <div className="pt-1">
                              <Button
                                variant="outline"
                                size="xs"
                                className="h-6 text-[11px]"
                                disabled={wallActionPostId === post.id}
                                onClick={() => handleToggleWallPostVisibility(post)}
                              >
                                {post.is_hidden ? <><Eye className="h-3 w-3 mr-1" />Unhide</> : <><EyeOff className="h-3 w-3 mr-1" />Hide</>}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Celebration Template Picker (Events) */}
      {showCelebrationTemplatePicker && (
        <EventTemplatePicker
          open={showCelebrationTemplatePicker}
          onClose={() => setShowCelebrationTemplatePicker(false)}
          entityType="celebration"
          entityId={celebration.id}
          onManualEntry={() => {
            setShowCelebrationTemplatePicker(false);
            setShowManualEventPopup(true);
          }}
          existingEvents={celebration.events.map(e => ({
            id: e.id,
            title: e.title,
            description: e.description,
            start_time: e.start_time,
          }))}
          onApplied={() => {
            // Reload celebration data to show new events
            loadCelebrationData(celebrationIdNum);
          }}
        />
      )}

      {/* Manual Event Create Popup */}
      {showManualEventPopup && celebration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b shrink-0">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={backToTemplateSelection}
                  aria-label="Back"
                  title="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <h3 className="font-semibold text-lg">{t('celebrations:details.manualEvent.title')}</h3>
                  <p className="text-sm text-muted-foreground">{t('celebrations:details.manualEvent.description')}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon-sm" onClick={() => setShowManualEventPopup(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 sm:p-5">
              <EventForm
                mode="create"
                embedded
                celebrationIdOverride={celebration.id}
                onCancel={backToTemplateSelection}
                onCreated={() => {
                  setShowManualEventPopup(false);
                  loadCelebrationData(celebrationIdNum);
                }}
              />
            </div>
          </div>
        </div>
      )}

    </PageContainer>
  );
};
